import { noop } from 'lodash-es';
import React, { createContext, FC, useCallback, useContext, useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { fetchFoldersFromSharedContent, useQueryFolders } from '../../hooks/folderQueries';
import { RequestStatus } from '../../types';
import {
  getFolderHierarchy,
  getSharedFolderHierarchy,
  useGetFolderHierarchy,
} from '../services/UploadManagementService';
import {
  moveFolder,
  moveGallery,
  movePanorama,
  moveWebStandalone,
} from '../services/UploadManagementService';
import {
  ContentDetails,
  CurrentFolder,
  CurrentFolderHierarchy,
  CurrentUploadContent,
  UploadView,
} from '../services/UploadManagementServiceTypes';
import {
  Content,
  isFolderContent,
  isGalleryContent,
  isPanoramaContent,
  isWebStandaloneContent,
} from '../services/UploadManagementServiceTypes';
import { getStatusFromQueries } from './getStatusFromQueries';
import { defaultSortState, useSortSettings } from '../../hooks/useSortSettings';
import { defaultDataView, useDataView } from '../../hooks/useDataView';

export interface UploadManagementContextProps
  extends ReturnType<typeof useSortSettings>,
    ReturnType<typeof useDataView> {
  currentFolderHierarchy?: CurrentFolderHierarchy;
  currentFolder?: CurrentFolder;
  getFolderListing: () => void;
  requestStatus: RequestStatus;
  view: UploadView;
  contentDetails: ContentDetails;
  shareGuid?: string;
  selectGuid: (guid: string) => void;
  deselectGuid: (guid: string) => void;
  moveSelectedToGuid: (guid: string) => Promise<void>;
}

export const UploadManagementContext = createContext<UploadManagementContextProps>({
  getFolderListing: noop,
  requestStatus: 'none',
  view: 'default',
  contentDetails: {},
  ...defaultSortState,
  dataView: defaultDataView,
  setDataView: noop,
  setSort: noop,
  selectGuid: noop,
  deselectGuid: noop,
  moveSelectedToGuid: () => Promise.resolve(),
});

interface ProviderProps {
  contentQueryResult: UseQueryResult<CurrentUploadContent>;
  folderHierarchyQueryResult: UseQueryResult<CurrentFolderHierarchy>;
  view: UploadView;
  shareGuid?: string;
}

export const Provider: FC<ProviderProps> = ({
  contentQueryResult,
  folderHierarchyQueryResult,
  view,
  shareGuid,
  children,
}) => {
  const currentFolderGuid = contentQueryResult.data?.folderLike?.guid;
  const dataView = useDataView();
  const sortSettings = useSortSettings(currentFolderGuid);

  const getFolderListing = () => {
    contentQueryResult.refetch();
    folderHierarchyQueryResult.refetch();
  };

  const requestStatus = getStatusFromQueries(contentQueryResult, folderHierarchyQueryResult);
  const dragAndDropValues = useDragAndDropSupport(
    contentQueryResult.data?.folderLike?.contents || [],
    getFolderListing,
  );

  return (
    <UploadManagementContext.Provider
      value={{
        requestStatus,
        currentFolder: contentQueryResult.data?.folderLike,
        contentDetails: contentQueryResult.data?.contentDetails || {},
        getFolderListing,
        view,
        currentFolderHierarchy: folderHierarchyQueryResult.data,
        shareGuid,
        ...sortSettings,
        ...dataView,
        ...dragAndDropValues,
      }}
    >
      {children}
    </UploadManagementContext.Provider>
  );
};

export const UploadManagementContextFromGuid: React.FunctionComponent<{ guid?: string }> = ({
  children,
  guid,
}) => {
  const contentQueryResult = useQueryFolders(guid);
  const folderHierarchyQueryResult = useGetFolderHierarchy(getFolderHierarchy, 'global');
  return (
    <Provider
      view='default'
      contentQueryResult={contentQueryResult}
      folderHierarchyQueryResult={folderHierarchyQueryResult}
    >
      {children}
    </Provider>
  );
};

export const UploadManagementContextFromSharedContent: React.FunctionComponent<{
  shareGuid?: string;
  contentGuid?: string;
}> = ({ children, contentGuid, shareGuid }) => {
  const contentQueryResult = useQuery({
    queryKey: [`fetchFoldersFromSharedContent`, contentGuid, shareGuid],
    queryFn: fetchFoldersFromSharedContent({ contentGuid, shareGuid }),
  });
  const folderHierarchyQueryResult = useQuery({
    queryKey: [`fetchFoldersFromSharedContent`, shareGuid],
    queryFn: () => getSharedFolderHierarchy(shareGuid),
  });
  return (
    <Provider
      view='shared'
      contentQueryResult={contentQueryResult}
      folderHierarchyQueryResult={folderHierarchyQueryResult}
      shareGuid={shareGuid}
    >
      {children}
    </Provider>
  );
};

export const useUploadContext = () => useContext(UploadManagementContext);

function useDragAndDropSupport(contents: Content[], refresh: () => void) {
  const [selectedGuids, setSelectedGuids] = useState<string[]>([]);
  const getContentByGuid = useCallback(
    (guid: string) => contents.find((i) => i.guid === guid),
    [contents],
  );
  const selectGuid = useCallback(
    (guid: string) => setSelectedGuids((guids) => [...guids, guid]),
    [],
  );
  const deselectGuid = useCallback(
    (guid: string) => setSelectedGuids((guids) => guids.filter((g) => g !== guid)),
    [],
  );
  const moveSelectedToGuid = useCallback(
    async (targetGuid: string) => {
      const targetContent = getContentByGuid(targetGuid);
      if (!targetContent || !isFolderContent(targetContent)) {
        setSelectedGuids([]);
        return;
      }

      const moveContent = async (sourceContent: Content | undefined) => {
        if (!sourceContent) {
          return false;
        }

        switch (true) {
          case isFolderContent(sourceContent):
            return moveFolder(sourceContent.guid, targetGuid);

          case isGalleryContent(sourceContent):
            return moveGallery(sourceContent.guid, targetGuid);

          case isPanoramaContent(sourceContent):
            return movePanorama(sourceContent.guid, targetGuid);

          case isWebStandaloneContent(sourceContent):
            return moveWebStandalone(sourceContent.guid, targetGuid);
        }
      };

      const movePromises = selectedGuids
        .filter((g) => g !== targetGuid)
        .map(getContentByGuid)
        .map((c) => {
          return moveContent(c)
            .then(() => c && deselectGuid(c.guid))
            .catch(noop);
        });

      return Promise.all(movePromises).then(refresh);
    },
    [deselectGuid, getContentByGuid, refresh, selectedGuids],
  );

  return { selectGuid, deselectGuid, moveSelectedToGuid };
}
